import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AdminHeader from '../../components/navigation/AdminHeader';
import CompanyFormHeader from './components/CompanyFormHeader';
import BasicInfoSection from './components/BasicInfoSection';
import LogoUploadSection from './components/LogoUploadSection';
import BrandColorsSection from './components/BrandColorsSection';
import ContactInfoSection from './components/ContactInfoSection';
import CTASettingsSection from './components/CTASettingsSection';
import StatusToggleSection from './components/StatusToggleSection';
import FormActions from './components/FormActions';
import { companyService } from '../../services/companyService';

const CompanyCreateEdit = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const companyId = searchParams?.get('id');
  const isEditMode = !!companyId;

  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: '',
    logo: null,
    primaryColor: '#2563EB',
    secondaryColor: '#64748B',
    email: '',
    phone: '',
    website: '',
    address: '',
    ctaText: 'Get a Free Quote',
    ctaUrl: '',
    isActive: true
  });

  const [errors, setErrors] = useState({});
  const [hasChanges, setHasChanges] = useState(false);
  const [initialData, setInitialData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    const adminToken = localStorage.getItem('adminToken');
    if (!adminToken) {
      navigate('/admin-login');
      return;
    }

    if (isEditMode && companyId) {
      loadCompanyData(companyId);
    }
  }, [companyId, isEditMode, navigate]);

  const loadCompanyData = async (id) => {
    try {
      setLoading(true);
      setLoadError('');
      const company = await companyService?.getById(id);
      setFormData(company);
      setInitialData(company);
    } catch (err) {
      console.error('Error loading company:', err);
      setLoadError(err?.message || 'Failed to load company data');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e?.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
    setHasChanges(true);

    if (errors?.[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleLogoChange = (logoData) => {
    setFormData((prev) => ({
      ...prev,
      logo: logoData
    }));
    setHasChanges(true);
  };

  const handleStatusChange = (isActive) => {
    setFormData((prev) => ({
      ...prev,
      isActive
    }));
    setHasChanges(true);
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.name?.trim()) {
      newErrors.name = 'Company name is required';
    }

    if (!formData?.slug?.trim()) {
      newErrors.slug = 'Slug-ID is required';
    } else if (!/^[a-z0-9-]+$/?.test(formData?.slug)) {
      newErrors.slug = 'Slug can only contain lowercase letters, numbers, and hyphens';
    }

    if (formData?.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.email)) {
      newErrors.email = 'Invalid email format';
    }

    if (formData?.website && !/^https?:\/\/.+/?.test(formData?.website)) {
      newErrors.website = 'Website must start with http:// or https://';
    }

    if (formData?.ctaUrl && !/^https?:\/\/.+/?.test(formData?.ctaUrl)) {
      newErrors.ctaUrl = 'CTA URL must start with http:// or https://';
    }

    if (formData?.primaryColor && !/^#[0-9A-F]{6}$/i?.test(formData?.primaryColor)) {
      newErrors.primaryColor = 'Invalid hex color format';
    }

    if (formData?.secondaryColor && !/^#[0-9A-F]{6}$/i?.test(formData?.secondaryColor)) {
      newErrors.secondaryColor = 'Invalid hex color format';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setSaving(true);
      
      if (isEditMode && companyId) {
        // Update existing company
        await companyService?.update(companyId, formData);
      } else {
        // Create new company
        await companyService?.create(formData);
      }

      // Navigate back to admin dashboard after successful save
      navigate('/admin-dashboard');
    } catch (err) {
      console.error('Error saving company:', err);
      setErrors({ submit: err?.message || 'Failed to save company' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <AdminHeader />
        <div className="main-content flex items-center justify-center py-20">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-text-secondary">Loading company data...</p>
          </div>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="min-h-screen bg-background">
        <AdminHeader />
        <div className="main-content flex items-center justify-center py-20">
          <div className="text-center">
            <div className="bg-red-50 text-red-600 px-6 py-4 rounded-lg mb-4">
              {loadError}
            </div>
            <button
              onClick={() => navigate('/admin-dashboard')}
              className="btn-secondary"
            >
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <AdminHeader />
      <div className="main-content">
        <CompanyFormHeader
          isEditMode={isEditMode}
          companyName={formData?.name}
        />

        {errors?.submit && (
          <div className="max-w-7xl mx-auto px-6 mb-4">
            <div className="bg-red-50 text-red-600 px-4 py-3 rounded-lg">
              {errors?.submit}
            </div>
          </div>
        )}

        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="space-y-6">
            <BasicInfoSection
              formData={formData}
              errors={errors}
              onChange={handleChange}
            />

            <LogoUploadSection
              logo={formData?.logo}
              onLogoChange={handleLogoChange}
              error={errors?.logo}
            />

            <BrandColorsSection
              formData={formData}
              errors={errors}
              onChange={handleChange}
            />

            <ContactInfoSection
              formData={formData}
              errors={errors}
              onChange={handleChange}
            />

            <CTASettingsSection
              formData={formData}
              errors={errors}
              onChange={handleChange}
            />

            <StatusToggleSection
              isActive={formData?.isActive}
              onChange={handleStatusChange}
            />
          </div>
        </div>

        <FormActions
          onSave={handleSave}
          isEditMode={isEditMode}
          hasChanges={hasChanges}
          saving={saving}
        />
      </div>
    </div>
  );
};

export default CompanyCreateEdit;